import { useState } from 'react'
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@agms/ui'
import { useEmailDocument } from '../../hooks/useDocuments'
import type { LeadDocument } from '../../lib/api'

interface Props {
  document: LeadDocument
  onClose: () => void
}

/**
 * Modal for emailing a document to an external recipient. Queues a
 * `SendDocumentEmailJob` on the `default` queue via the backend —
 * returns 202 immediately, we close on success rather than waiting
 * for delivery.
 */
export function EmailDocumentModal({ document, onClose }: Props) {
  const emailDoc = useEmailDocument()

  const [recipientEmail, setRecipientEmail] = useState('')
  const [recipientName, setRecipientName] = useState('')
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const isPending = emailDoc.isPending

  const handleSubmit = () => {
    setError(null)
    if (!recipientEmail || !recipientEmail.includes('@')) {
      setError('Enter a valid recipient email address.')
      return
    }

    emailDoc.mutate(
      {
        documentId: document.id,
        payload: {
          recipient_email: recipientEmail,
          recipient_name: recipientName || undefined,
          subject: subject || undefined,
          message: message || undefined,
        },
      },
      {
        onSuccess: () => {
          setSuccess(`Queued — will be delivered to ${recipientEmail} shortly.`)
        },
        onError: (err: unknown) => {
          const msg =
            err && typeof err === 'object' && 'message' in err
              ? String((err as { message: unknown }).message)
              : 'Failed to send email.'
          setError(msg)
        },
      },
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <Card className="w-full max-w-lg mx-4">
        <CardHeader>
          <CardTitle>Email document</CardTitle>
        </CardHeader>
        <CardContent>
          {success ? (
            <div className="space-y-4">
              <div className="rounded-md bg-green-50 border border-green-200 p-3 text-sm text-green-800" aria-live="polite">
                {success}
              </div>
              <div className="flex justify-end">
                <Button onClick={onClose}>Close</Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="text-xs text-gray-500">
                Sending <span className="font-medium text-gray-700">{document.file_name}</span>. The
                recipient will receive a secure link that expires in 2 hours.
              </div>

              <Input
                label="Recipient email"
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                placeholder="underwriter@example.com"
                required
              />

              <Input
                label="Recipient name (optional)"
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
                placeholder="Jane Smith"
              />

              <Input
                label="Subject (optional)"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={`Document from AGMS: ${document.file_name}`}
              />

              <div>
                <label htmlFor="email-document-message" className="block text-sm font-medium text-gray-700 mb-1">
                  Message (optional)
                </label>
                <textarea
                  id="email-document-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={4}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Please find the requested document attached via the secure link below."
                />
              </div>

              {error && (
                <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800" role="alert">
                  {error}
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button variant="secondary" onClick={onClose} disabled={isPending}>
                  Cancel
                </Button>
                <Button onClick={handleSubmit} isLoading={isPending}>
                  Send email
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
